import { InvoiceRow, ExcelRange } from '@/types'

/**
 * Expected column names (normalized)
 */
const COLUMN_MAPPING: Record<string, keyof InvoiceRow> = {
  'supplier name': 'supplierName',
  'supplier name ': 'supplierName', // with trailing space
  'supplier': 'supplierName',
  'lieferant': 'supplierName',
  'date invoice': 'dateInvoice',
  'date': 'dateInvoice',
  'datum': 'dateInvoice',
  'rechnungsdatum': 'dateInvoice',
  'brutto': 'brutto',
  'netto': 'netto',
  'vat': 'vatAmount',
  'vat amount': 'vatAmount',
  'mwst': 'vatAmount',
  'ust': 'vatAmount',
  'tax free': 'taxFreeAmount',
  'tax-free': 'taxFreeAmount',
  'steuerfrei': 'taxFreeAmount',
  'taxable net': 'taxableNetAmount',
  'taxable net amount': 'taxableNetAmount',
  'steuerpflichtig netto': 'taxableNetAmount',
  'rechnungsnummer': 'rechnungsnummer',
  'invoice number': 'rechnungsnummer',
  'invoice no': 'rechnungsnummer',
  'invoice-number': 'rechnungsnummer',
  'comment': 'comment',
  'comment js': 'comment', // match "Comment JS"
  'comments': 'comment',
  'kommentar': 'comment',
  'bemerkung': 'comment',
  'notiz': 'comment',
}

/**
 * Normalize string for comparison
 */
function normalize(str: string): string {
  return str.toLowerCase().trim().replace(/\s+/g, ' ')
}

/**
 * Parse German/European number format (1.234,56 or 1234.56)
 */
function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (typeof value === 'number') {
    return isNaN(value) ? null : value
  }

  const str = String(value).trim()

  // Remove thousand separators and replace decimal comma
  // German format: 1.234,56 -> 1234.56
  // English format: 1,234.56 -> 1234.56
  const normalized = str
    .replace(/\.(?=\d{3}(?:,|$))/g, '') // Remove dots as thousand separators
    .replace(/,(?=\d{3}(?:\.|$))/g, '') // Remove commas as thousand separators
    .replace(/,/g, '.') // Replace comma decimal separator with dot
    .replace(/[^\d.-]/g, '') // Remove currency symbols etc.

  const parsed = parseFloat(normalized)
  return isNaN(parsed) ? null : parsed
}

/**
 * Parse date from various formats
 */
function parseDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  // Excel serial date (number of days since 1900)
  if (typeof value === 'number') {
    // Sanity check - Excel dates are roughly between 1 (1900) and 70000 (2091)
    if (value < 1 || value > 100000) {
      console.warn('Invalid Excel serial date:', value)
      return null
    }
    // Excel epoch starts at 1900-01-01, but there's a bug where 1900 is treated as leap year
    // Adjust by subtracting 2 days: one for Excel's bug, one for JavaScript's epoch starting at 1970
    const excelEpoch = new Date(1900, 0, 1)
    const daysDiff = value - 2
    const date = new Date(excelEpoch.getTime() + daysDiff * 24 * 60 * 60 * 1000)
    return isNaN(date.getTime()) ? null : date
  }

  const str = String(value).trim()

  // Try parsing various formats
  const formats = [
    // ISO formats
    /^\d{4}-\d{2}-\d{2}$/,
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/,
    // German formats
    /^\d{1,2}\.\d{1,2}\.\d{4}$/,
    /^\d{1,2}\.\d{1,2}\.\d{2}$/,
    // US formats
    /^\d{1,2}\/\d{1,2}\/\d{4}$/,
  ]

  for (const format of formats) {
    if (format.test(str)) {
      const date = new Date(str)
      if (!isNaN(date.getTime())) {
        return date
      }
    }
  }

  // Try manual parsing for DD.MM.YYYY
  const dmyMatch = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    if (!isNaN(date.getTime())) {
      return date
    }
  }

  // Final fallback
  const date = new Date(str)
  return isNaN(date.getTime()) ? null : date
}

/**
 * Map Excel range values to InvoiceRow objects
 */
export function mapExcelRangeToInvoices(range: ExcelRange): InvoiceRow[] {
  try {
    if (!range.values || range.values.length < 2) {
      return []
    }

    const [headers, ...dataRows] = range.values

  // Build column index mapping
  const columnMap: Array<keyof InvoiceRow | null> = []
  const availableColumns: Set<keyof InvoiceRow> = new Set()

  for (let i = 0; i < headers.length; i++) {
    const header = normalize(String(headers[i] || ''))
    const mappedColumn = COLUMN_MAPPING[header]

    if (mappedColumn && !availableColumns.has(mappedColumn)) {
      columnMap[i] = mappedColumn
      availableColumns.add(mappedColumn)
    } else {
      columnMap[i] = null
    }
  }

  // Validate required columns
  const requiredColumns: (keyof InvoiceRow)[] = ['supplierName', 'dateInvoice', 'brutto', 'netto', 'rechnungsnummer']
  const missingColumns = requiredColumns.filter(col => !availableColumns.has(col))

  if (missingColumns.length > 0) {
    console.warn('Missing required columns:', missingColumns)
  }

  // Map data rows
  return dataRows
    .filter((row: unknown[]) => row && row.length > 0)
    .map((row: unknown[]) => {
      const invoice: Partial<InvoiceRow> = {
        supplierName: '',
        dateInvoice: null,
        brutto: null,
        netto: null,
        vatAmount: null,
        taxFreeAmount: null,
        taxableNetAmount: null,
        rechnungsnummer: '',
        comment: '',
      }

      for (let i = 0; i < row.length; i++) {
        const column = columnMap[i]
        if (!column) continue

        const value = row[i]

        switch (column) {
          case 'supplierName':
            invoice.supplierName = String(value || '').trim()
            break
          case 'dateInvoice':
            invoice.dateInvoice = parseDate(value)
            break
          case 'brutto':
            invoice.brutto = parseNumber(value)
            break
          case 'netto':
            invoice.netto = parseNumber(value)
            break
          case 'vatAmount':
            invoice.vatAmount = parseNumber(value)
            break
          case 'taxFreeAmount':
            invoice.taxFreeAmount = parseNumber(value)
            break
          case 'taxableNetAmount':
            invoice.taxableNetAmount = parseNumber(value)
            break
          case 'rechnungsnummer':
            invoice.rechnungsnummer = String(value || '').trim()
            break
          case 'comment':
            invoice.comment = String(value || '').trim()
            break
        }
      }

      // Skip rows without essential data
      if (!invoice.supplierName && !invoice.rechnungsnummer) {
        return null
      }

      return invoice as InvoiceRow
    })
    .filter((invoice: Partial<InvoiceRow> | null): invoice is InvoiceRow => invoice !== null)
  } catch (error) {
    console.error('Error parsing Excel data:', error)
    return []
  }
}

/**
 * Convert InvoiceRow array to CSV string
 */
export function invoicesToCSV(invoices: InvoiceRow[]): string {
  const headers = ['Supplier Name', 'Date Invoice', 'BRUTTO', 'NETTO', 'VAT Amount', 'Tax-Free Amount', 'Taxable Net Amount', 'Rechnungsnummer', 'Comment']

  const rows = invoices.map(invoice => [
    `"${String(invoice.supplierName).replace(/"/g, '""')}"`,
    invoice.dateInvoice ? invoice.dateInvoice.toISOString().split('T')[0] : '',
    String(invoice.brutto ?? ''),
    String(invoice.netto ?? ''),
    String(invoice.vatAmount ?? ''),
    String(invoice.taxFreeAmount ?? ''),
    String(invoice.taxableNetAmount ?? ''),
    `"${String(invoice.rechnungsnummer).replace(/"/g, '""')}"`,
    `"${String(invoice.comment).replace(/"/g, '""')}"`,
  ])

  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
}

/**
 * Download CSV file
 */
export function downloadCSV(invoices: InvoiceRow[], filename = 'invoices-export.csv'): void {
  const csv = invoicesToCSV(invoices)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
